import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import CheckFactorGraph from './CheckFactorGraph';
import AccelCurveGraph from './AccelCurveGraph';
import { PM5ForceCurveLive } from './PM5ForceCurveLive';
import { rhythmZoneColor } from '../lib/rhythmRatio';

// Portal-rendered dropdown that escapes any overflow-hidden ancestor and
// auto-flips above the trigger when there's not enough room below. Clamps
// horizontally to the viewport so it never gets cut off by the edge of
// the screen — see iter-67 field screenshot of P30 tile dropdown clipped
// by its parent card.
function PortalMenu({ anchorRef, open, onClose, minWidth = 200, children }) {
  const menuRef = useRef(null);
  const [style, setStyle] = useState({ top: 0, left: 0, visibility: 'hidden' });

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return;
    const compute = () => {
      const rect = anchorRef.current.getBoundingClientRect();
      const menuH = menuRef.current?.offsetHeight || 300;
      const menuW = menuRef.current?.offsetWidth || minWidth;
      const vpH = window.innerHeight;
      const vpW = window.innerWidth;
      // Prefer below the trigger; flip above if not enough room.
      const spaceBelow = vpH - rect.bottom;
      const openUp = spaceBelow < menuH + 12 && rect.top > menuH + 12;
      let top = openUp ? rect.top - menuH - 8 : rect.bottom + 8;
      // Centre on trigger, then clamp so the whole menu is on-screen.
      let left = rect.left + rect.width / 2 - menuW / 2;
      left = Math.max(8, Math.min(left, vpW - menuW - 8));
      top = Math.max(8, Math.min(top, vpH - menuH - 8));
      setStyle({ top, left, visibility: 'visible' });
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [open, anchorRef, minWidth]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) &&
          anchorRef.current && !anchorRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;
  return createPortal(
    <div
      ref={menuRef}
      className="bg-white dark:bg-zinc-800 border-4 border-zinc-900 dark:border-zinc-600 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] max-h-[60vh] overflow-y-auto"
      style={{ position: 'fixed', minWidth, zIndex: 9999, touchAction: 'pan-y', WebkitOverflowScrolling: 'touch', ...style }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body
  );
}

// Round a stroke-rate sample to its nearest half-step (22, 22.5, 23 …).
// Matches the convention NK SpeedCoach / RP3 use — half precision is the
// finest meaningful granularity for human-readable SPM.
const roundSpmHalf = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n * 2) / 2;
};

// Split a half-stepped stroke rate into renderable parts. The fraction
// glyph is rendered at 75 % size by the caller for the visual cue rowers
// expect from competitor monitors.
const splitStrokeRate = (v) => {
  if (typeof v !== 'number') return { whole: String(v ?? '--') };
  const half = roundSpmHalf(v);
  const whole = Math.floor(half);
  const hasHalf = half - whole >= 0.5;
  return hasHalf ? { whole: String(whole), fraction: '½' } : { whole: String(whole) };
};

// Abbreviated names for side metrics display
const METRIC_ABBREVIATIONS = {
  'split': 'SPL',
  'stroke_rate': 'S/M',
  'distance': 'DIST',
  'meters_per_second': 'SPD',
  'speed_kmh': 'KM/H',
  'heart_rate': 'HR',
  'watts': 'PWR',
  'power': 'PWR',
  'stroke_length': 'S/L',
  'check_factor': 'CHK',
  'accel_curve': 'ACC',
  'pm5_force_curve': 'PM5F',
  'time': 'ELAP',
  'pm5_time': 'PM5T',
  'calories': 'CAL',
  'avg_split': 'AVG',
  'peak_power': 'PEAK',
  'strokes': 'ST',
  'distance_per_stroke': 'D/S',
  'work_per_stroke': 'W/S',
  'zone': 'ZONE',
  'avg_split_3': 'AV3',
  'projected_finish': 'PROJ',
  'projected_30min': 'P30',
  'rhythm_ratio': 'RHY',
  'wbt_percent': '%WBT',
  // Oarlock per-side metrics (NK EmPower / XBoat power gates)
  'peak_force_port': 'PF-B',
  'peak_force_starboard': 'PF-S',
  'peak_force_sweep': 'PF',
  'catch_angle_port': 'CA-B',
  'catch_angle_starboard': 'CA-S',
  'catch_angle_sweep': 'CA'
};

// All 15 metrics + additional useful ones
const ALL_METRICS_CONFIG = [
  // Core 15 metrics from CrewZ
  { id: 'split', label: 'Split/500m', unit: 'MIN', formatter: (v) => v },
  { id: 'stroke_rate', label: 'Stroke Rate', unit: 'SPM', formatter: (v) => v },
  { id: 'distance', label: 'Distance', unit: 'M', formatter: (v) => Math.floor(v) },
  { id: 'meters_per_second', label: 'Speed', unit: 'M/S', formatter: (v) => v },
  { id: 'speed_kmh', label: 'Speed', unit: 'KM/H', formatter: (v) => v },
  { id: 'heart_rate', label: 'Heart Rate', unit: 'BPM', formatter: (v) => v },
  { id: 'watts', label: 'Power', unit: 'W', formatter: (v) => v },
  { id: 'power', label: 'Power/Rower', unit: 'W', formatter: (v) => v },
  { id: 'stroke_length', label: 'Stroke Length', unit: 'M', formatter: (v) => v },
  { id: 'check_factor', label: 'Check Factor', unit: 'M/S²', formatter: (v) => v, isGraph: true },
  { id: 'time', label: 'Elapsed', unit: 'HH:MM:SS', formatter: (v) => v },
  { id: 'pm5_time', label: 'PM5 Time', unit: 'HH:MM:SS', formatter: (v) => v },
  { id: 'calories', label: 'Calories', unit: 'KCAL', formatter: (v) => Math.round(v) },
  { id: 'avg_split', label: 'Avg Split', unit: 'MIN', formatter: (v) => v },
  { id: 'peak_power', label: 'Peak Power', unit: 'W', formatter: (v) => v },
  { id: 'strokes', label: 'Stroke Count', unit: '', formatter: (v) => v },
  { id: 'distance_per_stroke', label: 'Dist/Stroke', unit: 'M', formatter: (v) => v },
  { id: 'work_per_stroke', label: 'Work/Stroke', unit: 'J', formatter: (v) => v },
  // Additional useful metrics
  { id: 'zone', label: 'HR Zone', unit: '', formatter: (v) => v },
  { id: 'avg_split_3', label: 'Avg Split/3', unit: '3 STROKES', formatter: (v) => v },
  { id: 'projected_finish', label: 'Proj. Finish', unit: 'TIME', formatter: (v) => v },
  { id: 'projected_30min', label: 'Proj. 30:00', unit: 'M', formatter: (v) => Math.floor(v) },
  // Rhythm Ratio — drive : recovery, displayed as 1:X.X
  { id: 'rhythm_ratio', label: 'Rhythm Ratio', unit: 'DRIVE:REC', formatter: (v) => v },
  // Oarlock per-side metrics — populated only when a power gate (NK EmPower / XBoat) streams
  { id: 'peak_force_port', label: 'Peak Force (Bow)', unit: 'N', formatter: (v) => v },
  { id: 'peak_force_starboard', label: 'Peak Force (Stroke)', unit: 'N', formatter: (v) => v },
  { id: 'peak_force_sweep', label: 'Peak Force', unit: 'N', formatter: (v) => v },
  { id: 'catch_angle_port', label: 'Catch Angle (Bow)', unit: 'DEG', formatter: (v) => v },
  { id: 'catch_angle_starboard', label: 'Catch Angle (Stroke)', unit: 'DEG', formatter: (v) => v },
  { id: 'catch_angle_sweep', label: 'Catch Angle', unit: 'DEG', formatter: (v) => v }
];

const METRIC_OPTIONS = {
  large: [
    { id: 'split', label: 'Split/500m', unit: 'MIN', formatter: (v) => v },
    { id: 'time', label: 'Elapsed', unit: 'HH:MM:SS', formatter: (v) => v },
    { id: 'pm5_time', label: 'PM5 Time', unit: 'HH:MM:SS', formatter: (v) => v },
    { id: 'avg_split', label: 'Avg Split', unit: 'MIN', formatter: (v) => v },
    { id: 'avg_split_3', label: 'Avg Split/3', unit: '3 STROKES', formatter: (v) => v },
    { id: 'distance', label: 'Distance', unit: 'M', formatter: (v) => Math.floor(v) },
    { id: 'stroke_rate', label: 'Stroke Rate', unit: 'SPM', formatter: (v) => v },
    { id: 'meters_per_second', label: 'Speed', unit: 'M/S', formatter: (v) => v },
    { id: 'speed_kmh', label: 'Speed', unit: 'KM/H', formatter: (v) => v },
    { id: 'wbt_percent', label: "% of World's Best Time", unit: '%WBT', formatter: (v) => v },
    { id: 'watts', label: 'Power', unit: 'W', formatter: (v) => v },
    { id: 'peak_power', label: 'Peak Power', unit: 'W', formatter: (v) => v },
    { id: 'projected_finish', label: 'Proj. Finish', unit: 'TIME', formatter: (v) => v },
    { id: 'projected_30min', label: 'Proj. 30:00', unit: 'M', formatter: (v) => Math.floor(v) },
    { id: 'rhythm_ratio', label: 'Rhythm Ratio', unit: 'DRIVE:REC', formatter: (v) => v },
    { id: 'check_factor', label: 'Check Factor', unit: 'GRAPH', formatter: (v) => v, isGraph: true },
    { id: 'accel_curve', label: 'Acceleration Curve', unit: 'GRAPH · 3 STROKES', formatter: (v) => v, isGraph: true },
    { id: 'pm5_force_curve', label: 'PM5 Force Curve', unit: 'LIVE GRAPH', formatter: (v) => v, isGraph: true },
    // Power gate side metrics
    { id: 'peak_force_port', label: 'Peak Force (Bow)', unit: 'N', formatter: (v) => v },
    { id: 'peak_force_starboard', label: 'Peak Force (Stroke)', unit: 'N', formatter: (v) => v },
    { id: 'peak_force_sweep', label: 'Peak Force', unit: 'N', formatter: (v) => v },
    { id: 'catch_angle_port', label: 'Catch Angle (Bow)', unit: 'DEG', formatter: (v) => v },
    { id: 'catch_angle_starboard', label: 'Catch Angle (Stroke)', unit: 'DEG', formatter: (v) => v },
    { id: 'catch_angle_sweep', label: 'Catch Angle', unit: 'DEG', formatter: (v) => v }
  ],
  medium: [
    { id: 'time', label: 'Elapsed', unit: 'HH:MM:SS', formatter: (v) => v },
    { id: 'pm5_time', label: 'PM5 Time', unit: 'HH:MM:SS', formatter: (v) => v },
    { id: 'stroke_rate', label: 'Stroke Rate', unit: 'SPM', formatter: (v) => v },
    { id: 'split', label: 'Split/500m', unit: 'MIN', formatter: (v) => v },
    { id: 'avg_split', label: 'Avg Split', unit: 'MIN', formatter: (v) => v },
    { id: 'avg_split_3', label: 'Avg Split/3', unit: '3 STROKES', formatter: (v) => v },
    { id: 'distance', label: 'Distance', unit: 'M', formatter: (v) => Math.floor(v) },
    { id: 'meters_per_second', label: 'Speed', unit: 'M/S', formatter: (v) => v },
  { id: 'speed_kmh', label: 'Speed', unit: 'KM/H', formatter: (v) => v },
    { id: 'wbt_percent', label: "% of World's Best Time", unit: '%WBT', formatter: (v) => v },
    { id: 'watts', label: 'Power', unit: 'W', formatter: (v) => v },
    { id: 'peak_power', label: 'Peak Power', unit: 'W', formatter: (v) => v },
    { id: 'projected_finish', label: 'Proj. Finish', unit: 'TIME', formatter: (v) => v },
    { id: 'projected_30min', label: 'Proj. 30:00', unit: 'M', formatter: (v) => Math.floor(v) },
    { id: 'rhythm_ratio', label: 'Rhythm Ratio', unit: 'DRIVE:REC', formatter: (v) => v },
    // Power gate side metrics
    { id: 'peak_force_port', label: 'Peak Force (Bow)', unit: 'N', formatter: (v) => v },
    { id: 'peak_force_starboard', label: 'Peak Force (Stroke)', unit: 'N', formatter: (v) => v },
    { id: 'peak_force_sweep', label: 'Peak Force', unit: 'N', formatter: (v) => v },
    { id: 'catch_angle_port', label: 'Catch Angle (Bow)', unit: 'DEG', formatter: (v) => v },
    { id: 'catch_angle_starboard', label: 'Catch Angle (Stroke)', unit: 'DEG', formatter: (v) => v },
    { id: 'catch_angle_sweep', label: 'Catch Angle', unit: 'DEG', formatter: (v) => v }
  ],
  small: [
    { id: 'heart_rate', label: 'Heart Rate', unit: 'BPM', formatter: (v) => v },
    { id: 'stroke_rate', label: 'Stroke Rate', unit: 'SPM', formatter: (v) => v },
    { id: 'meters_per_second', label: 'Speed', unit: 'M/S', formatter: (v) => v },
  { id: 'speed_kmh', label: 'Speed', unit: 'KM/H', formatter: (v) => v },
    { id: 'wbt_percent', label: "% of World's Best Time", unit: '%WBT', formatter: (v) => v },
    { id: 'distance', label: 'Distance', unit: 'M', formatter: (v) => Math.floor(v) },
    { id: 'watts', label: 'Power', unit: 'W', formatter: (v) => v },
    { id: 'peak_power', label: 'Peak Power', unit: 'W', formatter: (v) => v },
    { id: 'calories', label: 'Calories', unit: 'KCAL', formatter: (v) => Math.round(v) },
    { id: 'split', label: 'Split/500m', unit: 'MIN', formatter: (v) => v },
    { id: 'avg_split', label: 'Avg Split', unit: 'MIN', formatter: (v) => v },
    { id: 'avg_split_3', label: 'Avg Split/3', unit: '3 STROKES', formatter: (v) => v },
    { id: 'time', label: 'Elapsed', unit: 'HH:MM:SS', formatter: (v) => v },
    { id: 'pm5_time', label: 'PM5 Time', unit: 'HH:MM:SS', formatter: (v) => v },
    { id: 'zone', label: 'HR Zone', unit: '', formatter: (v) => v },
    { id: 'strokes', label: 'Stroke Count', unit: '', formatter: (v) => v },
    { id: 'distance_per_stroke', label: 'Dist/Stroke', unit: 'M', formatter: (v) => v },
    { id: 'stroke_length', label: 'Stroke Length', unit: 'M', formatter: (v) => v },
    { id: 'work_per_stroke', label: 'Work/Stroke', unit: 'J', formatter: (v) => v },
    { id: 'check_factor', label: 'Check Factor', unit: 'M/S²', formatter: (v) => v },
    { id: 'projected_finish', label: 'Proj. Finish', unit: 'TIME', formatter: (v) => v },
    { id: 'projected_30min', label: 'Proj. 30:00', unit: 'M', formatter: (v) => Math.floor(v) },
    { id: 'rhythm_ratio', label: 'Rhythm Ratio', unit: 'DRIVE:REC', formatter: (v) => v },
    // Power gate side metrics
    { id: 'peak_force_port', label: 'Peak Force (Bow)', unit: 'N', formatter: (v) => v },
    { id: 'peak_force_starboard', label: 'Peak Force (Stroke)', unit: 'N', formatter: (v) => v },
    { id: 'peak_force_sweep', label: 'Peak Force', unit: 'N', formatter: (v) => v },
    { id: 'catch_angle_port', label: 'Catch Angle (Bow)', unit: 'DEG', formatter: (v) => v },
    { id: 'catch_angle_starboard', label: 'Catch Angle (Stroke)', unit: 'DEG', formatter: (v) => v },
    { id: 'catch_angle_sweep', label: 'Catch Angle', unit: 'DEG', formatter: (v) => v }
  ],
  bottom: [
    { id: 'time', label: 'Elapsed', unit: '', formatter: (v) => v },
    { id: 'pm5_time', label: 'PM5 Time', unit: '', formatter: (v) => v },
    { id: 'strokes', label: 'Strokes', unit: '', formatter: (v) => v },
    { id: 'calories', label: 'Calories', unit: 'KCAL', formatter: (v) => Math.round(v) },
    { id: 'distance', label: 'Distance', unit: 'M', formatter: (v) => Math.floor(v) },
    { id: 'watts', label: 'Power', unit: 'W', formatter: (v) => v },
    { id: 'peak_power', label: 'Peak Power', unit: 'W', formatter: (v) => v },
    { id: 'wbt_percent', label: "% of World's Best Time", unit: '%WBT', formatter: (v) => v },
    { id: 'stroke_rate', label: 'Stroke Rate', unit: 'SPM', formatter: (v) => v },
    { id: 'heart_rate', label: 'Heart Rate', unit: 'BPM', formatter: (v) => v },
    { id: 'zone', label: 'HR Zone', unit: '', formatter: (v) => v },
    { id: 'meters_per_second', label: 'Speed', unit: 'M/S', formatter: (v) => v },
  { id: 'speed_kmh', label: 'Speed', unit: 'KM/H', formatter: (v) => v },
    { id: 'avg_split', label: 'Avg Split', unit: '', formatter: (v) => v },
    { id: 'avg_split_3', label: 'Avg Split/3', unit: '', formatter: (v) => v },
    { id: 'distance_per_stroke', label: 'Dist/Stroke', unit: 'M', formatter: (v) => v },
    { id: 'stroke_length', label: 'Stroke Length', unit: 'M', formatter: (v) => v },
    { id: 'work_per_stroke', label: 'Work/Stroke', unit: 'J', formatter: (v) => v },
    { id: 'check_factor', label: 'Check Factor', unit: 'M/S²', formatter: (v) => v },
    { id: 'projected_finish', label: 'Proj. Finish', unit: 'TIME', formatter: (v) => v },
    { id: 'projected_30min', label: 'Proj. 30:00', unit: 'M', formatter: (v) => Math.floor(v) },
    { id: 'rhythm_ratio', label: 'Rhythm Ratio', unit: 'DRIVE:REC', formatter: (v) => v },
    // Power gate side metrics
    { id: 'peak_force_port', label: 'Peak Force (Bow)', unit: 'N', formatter: (v) => v },
    { id: 'peak_force_starboard', label: 'Peak Force (Stroke)', unit: 'N', formatter: (v) => v },
    { id: 'peak_force_sweep', label: 'Peak Force', unit: 'N', formatter: (v) => v },
    { id: 'catch_angle_port', label: 'Catch Angle (Bow)', unit: 'DEG', formatter: (v) => v },
    { id: 'catch_angle_starboard', label: 'Catch Angle (Stroke)', unit: 'DEG', formatter: (v) => v },
    { id: 'catch_angle_sweep', label: 'Catch Angle', unit: 'DEG', formatter: (v) => v }
  ]
};

export default function ClickableMetric({ 
  metricType = 'small', // 'large', 'medium', 'small', 'bottom', 'side'
  defaultMetric,
  allMetrics = {},
  className = '',
  textSize = 'text-3xl',
  valueStyle = null,
  highlight = false,
  onPreferenceChange,
  strokeData = [], // For check factor graph
  layout = 'vertical', // 'vertical' (label on top) or 'horizontal' (label on left)
  labelText = null // Override label text (e.g., "ELAP" instead of full "Elapsed")
}) {
  const [currentMetricId, setCurrentMetricId] = useState(defaultMetric);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const lastTapRef = useRef(0);
  const longPressTimerRef = useRef(null);
  const doubleTapDelay = 300; // ms between taps for double-tap
  const longPressDelay = 2000; // 2 seconds for long press (more practical than 3)

  // Re-sync internal state when defaultMetric prop changes (e.g. active mode
  // switch restores a different saved layout).
  useEffect(() => {
    setCurrentMetricId(defaultMetric);
  }, [defaultMetric]);

  const availableMetrics = METRIC_OPTIONS[metricType] || METRIC_OPTIONS.small;

  // Legacy alias resolver — pref values saved before iter-62 may point at
  // metric ids that no longer exist in the small/medium/etc. option list
  // (e.g. 'power' was renamed to 'watts'). Without this mapping, ClickableMetric
  // falls through to the first option and users see a mislabeled tile
  // (e.g. "HEART RATE / BPM" showing wattage). See iter-61 & iter-62 reports.
  const LEGACY_METRIC_ALIASES = { power: 'watts', stroke_watts: 'watts' };
  const resolvedMetricId = LEGACY_METRIC_ALIASES[currentMetricId] || currentMetricId;

  const currentMetric = availableMetrics.find(m => m.id === resolvedMetricId) ||
                        availableMetrics.find(m => m.label === defaultMetric) ||
                        availableMetrics[0];

  // On-water power is an estimate until the new formula is decided.
  const estTag = resolvedMetricId === 'watts' && allMetrics.power_estimated
    ? <span className="lowercase font-semibold opacity-80" data-testid="pwr-est-tag">est.</span>
    : null;
  const displayValue = currentMetric.formatter(allMetrics[resolvedMetricId] || allMetrics[currentMetric.id] || 0);

  // Stroke rate gets the half-step + small-fraction treatment everywhere it
  // appears (large vertical, side horizontal, default vertical). Returns
  // null for non-SPM metrics so callers fall back to plain `{displayValue}`.
  const strokeRateParts = currentMetricId === 'stroke_rate'
    ? splitStrokeRate(allMetrics[currentMetricId] || allMetrics[currentMetric.id] || 0)
    : null;
  const renderStrokeRate = strokeRateParts && (
    <>
      {strokeRateParts.whole}
      {strokeRateParts.fraction && (
        <span style={{ fontSize: '0.75em', verticalAlign: 'top', marginLeft: '0.05em' }}>
          {strokeRateParts.fraction}
        </span>
      )}
    </>
  );
  
  // Check if this is the check factor graph metric
  const isAccelGraph = (currentMetricId === 'accel_curve' || currentMetricId === 'pm5_force_curve') && metricType === 'large';
  const isCheckFactorGraph = (currentMetricId === 'check_factor' || isAccelGraph) && metricType === 'large';

  // Rhythm Ratio coaching colour — derive from the raw numeric ratio so we
  // don't have to re-parse the "1:X.X" string. Falls back to "" (default
  // colour) for any other metric or when no ratio has been computed yet.
  const valueColorClass = currentMetricId === 'rhythm_ratio'
    ? rhythmZoneColor(allMetrics.rhythm_ratio_raw)
    : '';

  // Outside-tap close is handled by PortalMenu (it knows the portaled menu node).
  // A trigger-only listener here closed the menu on touchstart inside the menu (Android bug).

  // Long press handlers for touch devices
  const handleTouchStart = (e) => {
    longPressTimerRef.current = setTimeout(() => {
      setShowDropdown(true);
      // Vibrate if supported (mobile feedback)
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
    }, longPressDelay);
  };

  const handleTouchEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleTouchMove = () => {
    // Cancel long press if user moves finger
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  // Handle double-tap/double-click to open dropdown
  const handleTap = (e) => {
    e.stopPropagation();
    const now = Date.now();
    const timeSinceLastTap = now - lastTapRef.current;
    
    if (timeSinceLastTap < doubleTapDelay && timeSinceLastTap > 0) {
      // Double tap detected - open dropdown
      setShowDropdown(true);
      lastTapRef.current = 0; // Reset
    } else {
      // First tap - record time
      lastTapRef.current = now;
    }
  };

  // Also support standard double-click for desktop
  const handleDoubleClick = (e) => {
    e.stopPropagation();
    setShowDropdown(true);
  };

  const handleMetricSelect = (metricId) => {
    setCurrentMetricId(metricId);
    setShowDropdown(false);
    
    // Save preference
    if (onPreferenceChange) {
      onPreferenceChange(metricId);
    }
  };

  // Handle button tap — behaves like a click on desktop (no drag) and
  // ignores taps that were part of a scroll gesture on mobile.
  const handleButtonTap = (metricId) => (e) => {
    e.stopPropagation();
    handleMetricSelect(metricId);
  };

  // Render Check Factor Graph
  if (isCheckFactorGraph) {
    return (
      <div
        ref={dropdownRef}
        onClick={handleTap}
        onDoubleClick={handleDoubleClick}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
        className={`relative cursor-pointer ${isAccelGraph ? 'w-full h-full' : ''} ${className}`}
      >
        {currentMetricId === 'pm5_force_curve' && metricType === 'large' ? (
          <PM5ForceCurveLive />
        ) : isAccelGraph ? (
          <AccelCurveGraph isErg={!!allMetrics.is_erg} />
        ) : (
          <CheckFactorGraph 
            strokeData={strokeData}
            currentSpeed={allMetrics.meters_per_second || 0}
            strokeRate={Number(allMetrics.stroke_rate) || 0}
          />
        )}
        
        {/* Dropdown Menu — portaled to body so it escapes card overflow.
            Iter-68 fix: buttons fire on `onClick` (not `onPointerDown`) so
            a finger dragging to scroll doesn't accidentally select the
            first metric it touches. Browser click-cancel-on-scroll takes
            care of touch scrolling natively. */}
        <PortalMenu anchorRef={dropdownRef} open={showDropdown} onClose={() => setShowDropdown(false)} minWidth={200}>
          {availableMetrics.map((metric) => (
            <button
              key={metric.id}
              type="button"
              onClick={handleButtonTap(metric.id)}
              className={`w-full px-4 py-3 text-left border-b-2 border-zinc-200 dark:border-zinc-700 last:border-b-0 hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors ${
                currentMetricId === metric.id ? 'bg-zinc-900 dark:bg-zinc-600 text-white' : 'dark:text-white'
              }`}
            >
              <div className="font-bold text-sm" style={{ fontFamily: 'Archivo, sans-serif' }}>
                {metric.label}
              </div>
              {metric.unit && (
                <div className={`text-xs mt-0.5 ${currentMetricId === metric.id ? 'text-zinc-300' : 'text-zinc-500 dark:text-zinc-400'}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {metric.unit}
                </div>
              )}
            </button>
          ))}
        </PortalMenu>
      </div>
    );
  }

  // Horizontal layout (label on left, value on right) - for side metrics
  if (layout === 'horizontal') {
    // Get the abbreviated name from the mapping
    const abbreviation = METRIC_ABBREVIATIONS[currentMetricId] || currentMetric.label.substring(0, 4).toUpperCase();
    
    return (
      <div
        ref={dropdownRef}
        onClick={handleTap}
        onDoubleClick={handleDoubleClick}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
        className={`relative cursor-pointer hover:bg-zinc-200 dark:hover:bg-zinc-600 transition-colors rounded-lg ${
          highlight ? 'bg-yellow-50 dark:bg-yellow-900/30' : ''
        } ${className}`}
      >
        <div className="flex items-center justify-between w-full">
          <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 dark:text-zinc-500 flex items-center gap-0.5" style={{ fontFamily: 'Archivo, sans-serif' }}>
            {abbreviation}{estTag && <> {estTag}</>}
            <ChevronDown className="w-2 h-2 opacity-40" />
          </div>
          <div className={`${textSize} font-bold tracking-tight leading-none ${valueColorClass || 'text-zinc-700 dark:text-white'}`} style={{ fontFamily: 'JetBrains Mono, monospace', ...(valueStyle || {}) }}>
            {renderStrokeRate || displayValue}
          </div>
        </div>

        {/* Dropdown Menu — portaled to body so it escapes card overflow */}
        <PortalMenu anchorRef={dropdownRef} open={showDropdown} onClose={() => setShowDropdown(false)} minWidth={180}>
          {availableMetrics.map((metric) => (
            <button
              key={metric.id}
              type="button"
              onClick={handleButtonTap(metric.id)}
              className={`w-full px-3 py-2 text-left border-b border-zinc-200 dark:border-zinc-700 last:border-b-0 hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors ${
                currentMetricId === metric.id ? 'bg-zinc-900 dark:bg-zinc-600 text-white' : 'dark:text-white'
              }`}
            >
              <div className="font-bold text-xs" style={{ fontFamily: 'Archivo, sans-serif' }}>
                {metric.label}
              </div>
            </button>
          ))}
        </PortalMenu>
      </div>
    );
  }

  // Default vertical layout
  return (
    <div
      ref={dropdownRef}
      onClick={handleTap}
      onDoubleClick={handleDoubleClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
      className={`relative cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors ${
        highlight ? 'bg-yellow-50 dark:bg-yellow-900/30' : ''
      } ${className}`}
    >
      <div className="text-xs font-bold uppercase tracking-widest mb-1 text-zinc-500 dark:text-zinc-400 flex items-center justify-center gap-1" style={{ fontFamily: 'Archivo, sans-serif' }}>
        {labelText || currentMetric.label}{estTag && <> {estTag}</>}
        <ChevronDown className="w-3 h-3 opacity-50" />
      </div>
      <div className={`${textSize} font-bold tracking-tight ${valueColorClass || 'text-zinc-900 dark:text-white'}`} style={{ fontFamily: 'JetBrains Mono, monospace', ...(valueStyle || {}) }}>
        {renderStrokeRate || displayValue}
      </div>
      {currentMetric.unit && (
        <div className="text-sm font-semibold text-zinc-500 dark:text-zinc-400" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {currentMetric.unit}
        </div>
      )}

      {/* Dropdown Menu — portaled to body so it escapes card overflow */}
      <PortalMenu anchorRef={dropdownRef} open={showDropdown} onClose={() => setShowDropdown(false)} minWidth={200}>
        {availableMetrics.map((metric) => (
          <button
            key={metric.id}
            type="button"
            onClick={handleButtonTap(metric.id)}
            className={`w-full px-4 py-3 text-left border-b-2 border-zinc-200 dark:border-zinc-700 last:border-b-0 hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors ${
              currentMetricId === metric.id ? 'bg-zinc-900 dark:bg-zinc-600 text-white' : 'dark:text-white'
            }`}
          >
            <div className="font-bold text-sm" style={{ fontFamily: 'Archivo, sans-serif' }}>
              {metric.label}
            </div>
            {metric.unit && (
              <div className={`text-xs mt-0.5 ${currentMetricId === metric.id ? 'text-zinc-300' : 'text-zinc-500 dark:text-zinc-400'}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {metric.unit}
              </div>
            )}
          </button>
        ))}
      </PortalMenu>
    </div>
  );
}
